import { useQuery, useSuspenseQueries } from "@tanstack/react-query";
import React from "react";

import { QuickFilters } from "@/common/components/quick-filters/quick-filters";
import { useCompany } from "@/common/providers/company-provider";
import {
  CommodityRatesToCommodity,
  CurrencyRatesToCurrency1,
  GetCommodityRatesPeriod,
} from "@/generated/api/prices";

import { commodityQuery, currencyPairQuery } from "./stock-exchange.api";
import {
  useActiveFilter,
  useFilterClickHandler,
  useFilters,
} from "./stock-exchange.hooks";
import { getFilters } from "./stock-exchange.utils";

type CommoditySettledProvider = {
  data: ReturnType<
    Exclude<ReturnType<typeof commodityQuery>["select"], undefined>
  >[];
  status: "success" | "empty";
};

type BusyProvider<TData> = {
  currency: undefined;
  data: TData;
  status: "error" | "pending";
};

type CommodityProviderResult = CommoditySettledProvider & ProviderProps;
type CurrencyProviderResult = (
  | CurrencySettledProvider
  | BusyProvider<undefined>
) &
  ProviderProps;

type ProviderProps = {
  filters: React.ComponentProps<typeof QuickFilters>;
};

type CommodityProviderProps = {
  children: (result: CommodityProviderResult) => React.ReactNode;
  commodityId: CommodityRatesToCommodity;
  currency: CurrencyRatesToCurrency1;
  periods: readonly GetCommodityRatesPeriod[];
};

type CurrencySettledProvider = {
  data: ReturnType<
    Exclude<ReturnType<typeof currencyPairQuery>["select"], undefined>
  >;
  currency: CurrencyRatesToCurrency1;
  status: "success" | "empty";
};

const CurrencyProvider = ({
  children,
}: {
  children: (result: CurrencyProviderResult) => React.ReactNode;
}) => {
  const filtersWithoutToday = getFilters().filter(
    (filter) => filter.value !== "today",
  );
  const handleFilterClick = useFilterClickHandler(filtersWithoutToday);
  const activeFilter = useActiveFilter("currencyPair");
  const currencyPair = useDetectCurrency();
  const query = useQuery(
    currencyPairQuery({
      ...currencyPair,
      range: activeFilter.value,
    }),
  );

  if (query.status === "success") {
    return children({
      currency: currencyPair.currency2,
      data: query.data,
      filters: {
        activeFilter,
        filters: filtersWithoutToday,
        onClick: (i: number) => handleFilterClick("currencyPair", i),
      },
      status: query.data.rates.length < 2 ? "empty" : query.status,
    });
  }

  return children({
    currency: undefined,
    data: undefined,
    filters: {
      activeFilter,
      filters: filtersWithoutToday,
      onClick: (i: number) => handleFilterClick("currencyPair", i),
    },
    status: query.status,
  });
};

const CommodityProvider = ({
  currency,
  commodityId,
  periods,
  children,
}: CommodityProviderProps) => {
  const filters = useFilters({ commodityId });
  const queries = useSuspenseQueries({
    queries: periods.map((period) =>
      commodityQuery({
        commodity: commodityId,
        currency,
        period,
        range: filters.activeFilter.value,
      }),
    ),
  });

  const getStatus = () => {
    if (queries.every((q) => q.data.rates.length < 2)) {
      return "empty";
    }

    return "success";
  };

  return children({
    currency,
    data: queries.map((q) => q.data),
    filters,
    status: getStatus(),
  } as CommodityProviderResult);
};

const Filters = ({
  commodityId,
  children,
}: {
  commodityId: CommodityRatesToCommodity;
  children: (filters: ReturnType<typeof useFilters>) => React.ReactNode;
}) => {
  const filters = useFilters({ commodityId });
  return children(filters);
};

const useDetectCurrency = (): {
  currency1: CurrencyRatesToCurrency1;
  currency2: CurrencyRatesToCurrency1;
} => {
  const { country } = useCompany();

  switch (country.currency) {
    case "GBP":
      throw new Error("Not implemented");
    case "RON":
    case "CZK":
      return { currency1: "EUR", currency2: country.currency };
    default:
      return { currency1: "USD", currency2: "EUR" };
  }
};

export { CommodityProvider, CurrencyProvider, Filters };
